import { useClipboardCopy, useI18N } from "@sundae/react-hooks";
import {
  Button,
  Card,
  CheckCircledIcon,
  Collapsible,
  CopyIcon,
  CrossCircledIcon,
  DateI18N,
  ExclamationTriangleIcon,
  InfoCircledIcon,
  ScrollArea,
  Separator,
  Text,
  Tooltip,
} from "@sundae/ui-toolkit";
import { useWalletDetails } from "@sundae/wallet-react";
import { useMutation } from "@tanstack/react-query";
import cx from "classnames";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { serializeError } from "serialize-error";

import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { ERRORS_API_URL } from "../../../constants/api.constants";
import { testIds } from "../../../constants/testIds.constants";
import ErrorDetailsMessage from "../../../i18n/components/error/ErrorDetailsMessage/ErrorDetailsMessage.trans";
import { useProfileAppContext } from "../../../stores";
import { ETransactionState } from "../../../types/Transaction.types";
import Motion from "../../Motion/Motion";
import { useMigrateLiquidityContext } from "./context";
import { EErrorType } from "./types";

const ErrorBox = () => {
  const { t, currentLocale } = useI18N("transactions");
  const [reportID, setReportID] = useState<string>("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { copy, copied } = useClipboardCopy({ copiedTimeout: 500 });
  const {
    error,
    handlers: { setDialogState },
  } = useMigrateLiquidityContext();
  const {
    state: { errorReportingEnabled },
  } = useProfileAppContext();
  const { details: wallet, activeWallet } = useWalletDetails();

  const { message, originalError, details, type } = error || {};

  const serializedOriginalError = useMemo(
    () =>
      originalError &&
      JSON.stringify(serializeError(originalError), (_key, value) =>
        typeof value === "bigint" ? value.toString() : value
      ),
    [originalError]
  );

  const reportData = useMemo(() => {
    const formattedDate = format(new Date(), "MMM d, yyyy h:mm:ss aa z");
    return {
      ...(details && { details }),
      ...(originalError && { originalError: serializedOriginalError }),
      activeWallet,
      buildInfo: {
        ...window.__BUILD_CONFIG,
        buildAt: new Date(window.__BUILD_CONFIG.builtAt),
      },
      date: formattedDate,
      message,
      project: "DEX-V2",
      reportID,
      url: window.location.href,
      wallet: wallet?.mainAddress,
    };
  }, [message, wallet, reportID]);

  const {
    mutateAsync: handleReport,
    isLoading,
    isSuccess,
  } = useMutation([reportData], async () => {
    const response = await fetch(ERRORS_API_URL, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reportData),
    });

    if (response.status === 200 && response.ok) {
      const data = await response.json();
      setReportID(data.reportID);
      return;
    }
  });

  useEffect(() => {
    if (errorReportingEnabled && !isLoading && !isSuccess && type !== EErrorType.info) {
      handleReport();
    }
  }, [errorReportingEnabled, isLoading, isSuccess, type]);

  const getIcon = useCallback(() => {
    const commonClasses = "h-12 w-12 fill-current xl:h-20 xl:w-20";

    switch (type) {
      case EErrorType.error:
        return <CrossCircledIcon className={cx(commonClasses, "text-error dark:text-error")} />;
      case EErrorType.info:
        return (
          <InfoCircledIcon className={cx(commonClasses, "text-primary dark:text-primary-200")} />
        );
      case EErrorType.warning:
        return (
          <ExclamationTriangleIcon
            className={cx(commonClasses, "text-warning dark:text-warning-200")}
          />
        );
      default:
        return null;
    }
  }, [type]);

  const setResetState = useCallback(() => {
    setDialogState(ETransactionState.reset);
  }, []);
  const memoizedHandleReport = useCallback(() => handleReport(), []);

  return (
    <>
      <Motion className="relative z-10 h-full">
        <div className="flex h-full flex-col items-center justify-center gap-4 text-center xl:gap-5">
          {getIcon()}
          <div className="text-center">
            <div data-testid={testIds.yieldFarming.earnings.error.message}>
              {reportData.message}
            </div>
            <div className="mt-8 flex items-center justify-center gap-4">
              <Button className="w-fit" onClick={setResetState} variant="primary">
                {t("tryAgain", { ns: "common" })}
              </Button>
              {type !== EErrorType.info && (
                <Button
                  className="w-fit"
                  onClick={memoizedHandleReport}
                  variant="secondary-outlined"
                  disabled={Boolean(reportID) || errorReportingEnabled || isLoading}
                >
                  {isLoading && <ArrowPathIcon className="h-4 w-4 motion-safe:animate-spin" />}
                  {t(reportID ? "errors.generic.reported" : "errors.generic.report")}
                </Button>
              )}
            </div>
          </div>
          <Separator />
          {reportID && (
            <div className="flex items-center justify-center gap-4">
              <Text tag="h6" weight="bold">
                {t("errors.dataLabels.reportId")}:{" "}
                <Text tag="span" weight="normal">
                  {reportID}
                </Text>
              </Text>
              <Tooltip
                tooltipLabel={t("errors.generic.copyReportId")}
                trigger={
                  <>
                    {copied ? (
                      <CheckCircledIcon
                        className="fill-current !text-success"
                        height={20}
                        width={20}
                      />
                    ) : (
                      <CopyIcon
                        height={20}
                        onClick={() => copy(reportData?.reportID)}
                        role="button"
                        width={20}
                      />
                    )}
                  </>
                }
              />
            </div>
          )}
          {error && type !== EErrorType.info && (
            <Collapsible
              open={detailsOpen}
              trigger={
                <ErrorDetailsMessage toggleCollapsible={() => setDetailsOpen((prev) => !prev)} />
              }
            >
              <ScrollArea className="mt-2 rounded-lg">
                <Card className="whitespace-pre p-6">
                  <div className="grid gap-2">
                    <div>
                      <Text tag="div" variant="warning">
                        {t("errors.dataLabels.url")}
                      </Text>
                      <Text tag="span" className="whitespace-pre-wrap break-all">
                        {reportData.url}
                      </Text>
                    </div>
                    <div>
                      <Text tag="div" variant="warning">
                        {t("errors.dataLabels.date")}
                      </Text>
                      <DateI18N value={new Date(reportData.date)} locale={currentLocale} />
                    </div>
                    <div>
                      <Text tag="div" variant="warning">
                        {t("errors.dataLabels.project")}
                      </Text>
                      <Text tag="span" className="whitespace-pre-wrap break-all">
                        {reportData.project}
                      </Text>
                    </div>
                    <div>
                      <Text tag="div" variant="warning">
                        {t("errors.dataLabels.wallet")}
                      </Text>
                      <Text tag="span" className="whitespace-pre-wrap break-all">
                        {reportData.activeWallet}
                      </Text>
                    </div>
                    <div>
                      <Text tag="div" variant="warning">
                        {t("errors.dataLabels.address")}
                      </Text>
                      <Text tag="span" className="whitespace-pre-wrap break-all">
                        {reportData.activeWallet}
                      </Text>
                    </div>
                    <div>
                      <Text tag="div" variant="warning">
                        {t("errors.dataLabels.buildInfo")}
                      </Text>
                      <Text tag="span" className="whitespace-pre-wrap break-all">
                        {JSON.stringify(reportData.buildInfo)}
                      </Text>
                    </div>
                    {serializedOriginalError && (
                      <div>
                        <Text tag="div" variant="warning">
                          {t("errors.dataLabels.message")}
                        </Text>
                        <Text
                          tag="pre"
                          className="w-full whitespace-pre-wrap break-all"
                          style={{
                            overflowWrap: "break-word",
                          }}
                          size="xs"
                        >
                          {serializedOriginalError.slice(0, 500)}
                          {serializedOriginalError.length > 500 && "..."}
                        </Text>
                      </div>
                    )}
                  </div>
                </Card>
              </ScrollArea>
            </Collapsible>
          )}
        </div>
      </Motion>
    </>
  );
};

export default ErrorBox;
